import { EncryptStorage } from "encrypt-storage";
import axios from "axios";
import type { Unit } from "../types";
import { format } from "date-fns";
import { id, enUS } from "date-fns/locale";
import dayjs from "dayjs";

const isEmpty = (variable: unknown): boolean =>
  [null, "", undefined].includes(variable as any);
const isEmptyObjct = (obj: Record<string, unknown>): boolean =>
  Object.keys(obj).length === 0;
const hasProps = (
  obj: Record<string, unknown> | null | undefined,
  prop_name: string,
): boolean => Object.hasOwn(obj ?? {}, prop_name);
const changeState = <T extends Record<string, unknown>>(
  setState: (updater: (prev: T) => T) => void,
  data: Partial<T>,
): void => setState((prev) => ({ ...prev, ...data }) as T);
export const converParamUrlToObject = (param = ""): Record<string, string> =>
  Object.fromEntries(new URLSearchParams(param));
const keyPressNumberOnly = (event: KeyboardEvent): void => {
  const NumRegex = new RegExp("^([0-9])+$");
  if (!NumRegex.test(event.key) && !["Backspace", "Tab"].includes(event.key)) {
    event.preventDefault();
  }
};
const numberWithCommas = (
  number: number | string | null | undefined,
): string | undefined =>
  number?.toString()?.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
const objectToQuery = (obj: Record<string, unknown>): string => {
  const query = Object.entries(obj)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .map(
            (v) =>
              `${encodeURIComponent(key)}[]=${encodeURIComponent(String(v))}`,
          )
          .join("&");
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
    })
    .join("&");
  return query;
};

const regexThousandSeparator = (number: string): RegExpMatchArray | null => {
  if (number?.split(".")?.length > 2) {
    number = number?.replace(/\.+$/, "");
  }
  const regex = /^[0-9]*\.?[0-9]*$/;
  const val = number.match(regex);
  return val || null;
};

const globalStorage = new EncryptStorage("global_params", {
  storageType: "localStorage",
  encAlgorithm: "Rabbit",
});

const fetchEncryptId = (id: unknown): unknown | false => {
  try {
    const params = id?.toString() || "";
    const code = JSON.parse(atob(params));
    return code;
  } catch (e) {
    return false;
  }
};

const convertDate = ({
  date,
  displayFormat = "yyyy-MM-dd",
  language = false,
}: {
  date?: string | number | Date;
  displayFormat?: string;
  language?: boolean;
}): string => {
  const defDate = date ? new Date(date) : new Date();
  const formatLanguage = language ? id : enUS;
  return format(defDate, displayFormat, { locale: formatLanguage });
};

const isValidDate = (date: unknown): boolean => {
  if ([undefined, null, "", {}].includes(date as any)) return false;
  return dayjs(date as any).isValid();
};

const convertToBase64 = (file: Blob): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const API: string = import.meta.env.VITE_URL_API as string;
const APIPayment: string = import.meta.env.VITE_URL_API_PAYMENT as string;
const APIImg: string = import.meta.env.VITE_URL_API_IMG as string;
const BaseImgURL: string = import.meta.env.VITE_BASE_URL_IMG as string;

interface NodeWithAction {
  action?: string;
  children?: NodeWithAction[];
}

const findNode = (
  param: string,
  currentNode: NodeWithAction[],
): NodeWithAction | undefined => {
  for (const node of currentNode) {
    if (node.action === param) return node;
    if (node.children) {
      const child = findNode(param, node.children);
      if (child) return child;
    }
  }
};

const fetchUnits = async (signal?: AbortSignal): Promise<Unit[]> => {
  const response = await axios.post(`${APIPayment}/unit/list`, {}, { signal });
  return (response.data.data ?? []).map((unit: Unit) => ({
    ...unit,
    unit_code: unit.code,
  }));
};

const formatUnitLabel = (
  unit?: {
    code?: string;
    name?: string;
    unit_code?: string;
    unit_name?: string;
  } | null,
): string => {
  if (!unit) return "-";
  const code = unit.code ?? unit.unit_code;
  const name = unit.name ?? unit.unit_name ?? "";
  return code ? `${code} - ${name}` : name || "-";
};

const fileSize = (size: number): string => {
  if (size === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(size) / Math.log(k));
  return parseFloat((size / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export { globalStorage, API, APIPayment, APIImg, BaseImgURL };
export {
  isEmpty,
  isEmptyObjct,
  hasProps,
  changeState,
  keyPressNumberOnly,
  isValidDate,
  objectToQuery,
};
export {
  numberWithCommas,
  regexThousandSeparator,
  fetchEncryptId,
  convertDate,
  fileSize,
  convertToBase64,
  findNode,
  fetchUnits,
  formatUnitLabel,
};
